import React, { useMemo } from 'react';
import { Icons } from '../../utils/icons';
import { ESTADO_PUESTA_MARCHA, estadoColorObra, COLORES_OBRA } from './utils/estadosObra';

const DIAS_ALERTA = 14; // a partir de cuántos días "registrado" sin agendar se avisa

function diasDesde(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function KpiCard({ icon, label, value, color }) {
  const IconComponent = Icons[icon];
  return (
    <div className="bg-white rounded-2xl shadow-lg border p-5">
      <div className="flex items-center gap-2 mb-1">
        <IconComponent className={`w-4 h-4 ${color || 'text-gray-400'}`} />
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{label}</p>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

export default function ObrasAnalitica({ obras, estadoDe }) {
  const kpis = useMemo(() => {
    let totalClientes = 0, completados = 0, obrasRojas = 0, extCount = 0;
    for (const obra of obras) {
      const clientes = obra.clientes || [];
      totalClientes += clientes.length;
      completados += clientes.filter(c => ['realizado', 'ext'].includes(estadoDe(c))).length;
      extCount += clientes.filter(c => estadoDe(c) === 'ext').length;
      if (estadoColorObra(clientes, estadoDe) === 'rojo') obrasRojas++;
    }
    const pctCompletado = totalClientes > 0 ? Math.round((completados / totalClientes) * 100) : 0;
    const pctExt = totalClientes > 0 ? Math.round((extCount / totalClientes) * 100) : 0;
    return { totalObras: obras.length, totalClientes, pctCompletado, obrasRojas, pctExt };
  }, [obras, estadoDe]);

  // Ranking por progreso — las que menos avanzan primero, para saber a
  // cuáles dedicar atención ya.
  const ranking = useMemo(() => {
    return obras.map(obra => {
      const clientes = obra.clientes || [];
      const completados = clientes.filter(c => ['realizado', 'ext'].includes(estadoDe(c))).length;
      const pct = clientes.length > 0 ? Math.round((completados / clientes.length) * 100) : 0;
      return { obra, total: clientes.length, completados, pct, color: estadoColorObra(clientes, estadoDe) };
    }).sort((a, b) => a.pct - b.pct || (b.total - a.total));
  }, [obras, estadoDe]);

  // Clientes "registrado" desde hace más de DIAS_ALERTA días, sin ningún
  // avance — señal de que se puede haber perdido el seguimiento.
  const alertas = useMemo(() => {
    const out = [];
    for (const obra of obras) {
      for (const c of obra.clientes || []) {
        if (estadoDe(c) !== 'registrado') continue;
        const dias = diasDesde(c.addedAt);
        if (dias !== null && dias >= DIAS_ALERTA) out.push({ obra, cliente: c, dias });
      }
    }
    return out.sort((a, b) => b.dias - a.dias);
  }, [obras, estadoDe]);

  return (
    <div className="space-y-4">
      {/* KPIs — resumen de un vistazo */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard icon="Layout" label="Obras activas" value={kpis.totalObras} />
        <KpiCard icon="Users" label="Clientes en seguimiento" value={kpis.totalClientes} />
        <KpiCard icon="Check" label="Completados" value={`${kpis.pctCompletado}%`} color="text-green-500" />
        <KpiCard icon="Tag" label="% EXT (convertidos a contrato)" value={`${kpis.pctExt}%`} color="text-[#B8860B]" />
        <KpiCard icon="Zap" label="Obras en rojo" value={kpis.obrasRojas} color="text-red-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Ranking por progreso */}
        <div className="bg-white rounded-2xl shadow-lg border p-5">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Icons.Layout className="w-4 h-4 text-gray-400" />Obras por progreso
          </h3>
          {ranking.length === 0 ? (
            <p className="text-sm text-gray-400">Todavía no hay obras registradas.</p>
          ) : (
            <div className="space-y-3">
              {ranking.map(({ obra, total, completados, pct, color }) => (
                <div key={obra.id}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-sm font-bold text-gray-800 truncate">{obra.titulo}</p>
                    <span className="text-xs font-bold text-gray-500 flex-shrink-0">
                      {total === 0 ? 'Sin clientes' : `${completados}/${total} · ${pct}%`}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: COLORES_OBRA[color] }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Alertas de seguimiento perdido */}
        <div className="bg-white rounded-2xl shadow-lg border p-5">
          <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
            <Icons.Bell className="w-4 h-4 text-amber-500" />Seguimiento perdido
          </h3>
          <p className="text-xs text-gray-400 mb-3">Clientes "Registrado" hace {DIAS_ALERTA}+ días sin agendarse</p>
          {alertas.length === 0 ? (
            <p className="text-sm text-gray-400">Ningún cliente lleva tanto tiempo sin avanzar. 👍</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {alertas.map(({ obra, cliente, dias }) => (
                <div key={`${obra.id}-${cliente.clienteId}`} className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 ${ESTADO_PUESTA_MARCHA.registrado.bg}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{cliente.clienteNombre}</p>
                    <p className="text-xs text-gray-500 truncate">{obra.titulo}</p>
                  </div>
                  <span className="text-xs font-bold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 flex-shrink-0">{dias}d</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
